<script setup>
import RailModules from 'dashboard/components-next/Conversation/RailModules.vue';
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import Button from 'dashboard/components-next/button/Button.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';
import { useMapGetter } from 'dashboard/composables/store';
import { useKeyboardEvents } from 'dashboard/composables/useKeyboardEvents';
import {
  useShortcutDock,
  DOCK_CATALOG,
  CONVERSATION_DRAG_TYPE,
} from 'dashboard/components-next/dock/useShortcutDock';

const { t } = useI18n();
const router = useRouter();
const route = useRoute();
const { updateUISettings } = useUISettings();

const currentAccountId = useMapGetter('getCurrentAccountId');
const getConversationById = useMapGetter('getConversationById');
const getAllConversations = useMapGetter('getAllConversations');
const { uiSettings } = useUISettings();
const isContactSidebarOpen = computed(
  () => uiSettings.value.is_contact_sidebar_open
);

const toggleConversationSidebarToggle = () => {
  if (isContactSidebarOpen.value) {
    updateUISettings({ is_contact_sidebar_open: false });
    return;
  }
  updateUISettings({
    is_contact_sidebar_open: true,
    is_copilot_panel_open: false,
  });
};

const handleConversationSidebarToggle = () => {
  toggleConversationSidebarToggle();
};

const keyboardEvents = {
  'Alt+KeyO': {
    action: toggleConversationSidebarToggle,
  },
};
useKeyboardEvents(keyboardEvents);

const {
  items,
  catalog,
  addItem,
  addConversation,
  removeItem,
  displayLabel,
  displayIcon,
} = useShortcutDock();

// V3 (mockup final-v3v2.html): com a flag ligada, os modulos da conversa que
// saiam do cabecalho passam a morar aqui, numa secao propria acima dos atalhos.
// O contato permanece no topo; o Copiloto fica oculto durante sua validacao.
const flagPorConta = useMapGetter('accounts/isFeatureEnabledonAccount');
const contaAtual = useMapGetter('getCurrentAccountId');
const isHeaderV3 = computed(() => {
  const isEnabled = flagPorConta.value;
  if (typeof isEnabled !== 'function' || !contaAtual.value) return false;
  return Boolean(
    isEnabled(contaAtual.value, FEATURE_FLAGS.CONVERSATION_HEADER_V3)
  );
});

const railRef = ref(null);
const isAddOpen = ref(false);
const isDragOver = ref(false);
let dragDepth = 0;

const catalogOpensUp = ref(false);
const catalogMaxHeightPx = ref(416);

const closeAddPanel = () => {
  isAddOpen.value = false;
};

const toggleAddPanel = event => {
  if (isAddOpen.value) {
    closeAddPanel();
    return;
  }
  const trigger = event.currentTarget.getBoundingClientRect();
  const gap = 12;
  const spaceBelow = Math.max(0, window.innerHeight - trigger.top - gap);
  const spaceAbove = Math.max(0, trigger.bottom - gap);
  const opensUp = spaceBelow < 280 && spaceAbove > spaceBelow;
  catalogOpensUp.value = opensUp;
  catalogMaxHeightPx.value = Math.max(
    180,
    Math.floor(opensUp ? spaceAbove : spaceBelow)
  );
  isAddOpen.value = true;
};

const onDocClickCatalog = event => {
  if (!isAddOpen.value) return;
  if (railRef.value?.contains(event.target)) return;
  closeAddPanel();
};

const onDocKeydown = event => {
  if (event.key === 'Escape' && isAddOpen.value) closeAddPanel();
};

onMounted(() => {
  document.addEventListener('click', onDocClickCatalog);
  document.addEventListener('keydown', onDocKeydown);
});

onBeforeUnmount(() => {
  document.removeEventListener('click', onDocClickCatalog);
  document.removeEventListener('keydown', onDocKeydown);
});

const isConversationDrag = event =>
  Boolean(event.dataTransfer?.types?.includes(CONVERSATION_DRAG_TYPE));

const onDragEnter = event => {
  if (!isConversationDrag(event)) return;
  dragDepth += 1;
  isDragOver.value = true;
};

const onDragLeave = () => {
  dragDepth = Math.max(0, dragDepth - 1);
  if (dragDepth === 0) isDragOver.value = false;
};

const onDrop = event => {
  dragDepth = 0;
  isDragOver.value = false;
  const raw = event.dataTransfer?.getData(CONVERSATION_DRAG_TYPE);
  if (!raw) return;
  event.preventDefault();
  try {
    const { id, label, thumbnail } = JSON.parse(raw);
    if (id) addConversation({ id, label, thumbnail });
  } catch {
    // payload estranho: ignora
  }
};

const go = item => {
  if (item.kind === 'conversation') {
    router.push({
      name: 'inbox_conversation',
      params: {
        accountId: currentAccountId.value,
        conversation_id: item.conversationId,
      },
    });
    return;
  }
  const entry = DOCK_CATALOG.find(c => c.key === item.key);
  if (!entry) return;
  router.push({
    name: entry.route,
    params: { accountId: currentAccountId.value },
    ...(entry.query ? { query: entry.query } : {}),
  });
};

const isItemActive = item => {
  if (item.kind === 'conversation') {
    return String(route.params.conversation_id) === String(item.conversationId);
  }
  const entry = DOCK_CATALOG.find(c => c.key === item.key);
  if (!entry || route.name !== entry.route) return false;
  if (!entry.query) return !route.query.view;
  return Object.entries(entry.query).every(
    ([key, value]) => String(route.query[key] || '') === String(value)
  );
};

const unreadCounts = computed(() => {
  const all = getAllConversations.value;
  const currentId = route.params.conversation_id;
  const counts = {};
  items.value.forEach(item => {
    if (item.kind !== 'conversation') return;
    if (String(item.conversationId) === String(currentId)) {
      counts[item.key] = 0;
      return;
    }
    const chat =
      getConversationById.value?.(item.conversationId) ||
      all?.find(entry => Number(entry.id) === Number(item.conversationId));
    const count = Number(chat?.unread_count || 0);
    counts[item.key] = count > 0 ? count : 0;
  });
  return counts;
});

const unreadBadge = count => (count > 9 ? '9+' : String(count));

const itemLabel = item => {
  const name = displayLabel(item);
  const count = unreadCounts.value[item.key] || 0;
  if (!count) return name;
  return t('DOCK.UNREAD', { name, count: unreadBadge(count) });
};

const onAddFromCatalog = entry => {
  addItem({ kind: 'destination', key: entry.key });
};
</script>

<template>
  <aside
    ref="railRef"
    class="relative hidden min-h-0 w-11 shrink-0 flex-col items-center rounded-xl border border-n-weak bg-n-surface-1 my-2 mr-2 py-3 md:flex"
    data-testid="conversation-workbench-rail"
    @dragenter.prevent="onDragEnter"
    @dragover.prevent
    @dragleave="onDragLeave"
    @drop.prevent="onDrop"
  >
    <div class="flex shrink-0 flex-col items-center gap-2 px-1">
      <Button
        v-tooltip.left="$t('CONVERSATION.SIDEBAR.CONTACT')"
        ghost
        slate
        sm
        class="!rounded-full transition-all duration-[250ms] ease-out active:!scale-95"
        :class="{
          'bg-n-alpha-2': isContactSidebarOpen,
        }"
        :icon="isContactSidebarOpen ? 'i-ph-user-fill' : 'i-ph-user-bold'"
        :aria-label="t('CONVERSATION.SIDEBAR.CONTACT')"
        @click="handleConversationSidebarToggle"
      />
    </div>

    <template v-if="isHeaderV3">
      <div class="my-2 w-6 shrink-0 border-t border-n-weak" />
      <RailModules />
    </template>

    <div class="my-2 w-6 shrink-0 border-t border-n-weak" />

    <div
      class="flex min-h-0 w-full flex-1 flex-col items-center"
      :class="{ 'rounded-lg bg-n-blue-9/10': isDragOver }"
      data-testid="shortcut-rail-drop"
    >
      <div
        class="flex min-h-0 w-full flex-col items-center gap-1 overflow-y-auto px-1"
      >
        <span
          v-for="item in items"
          :key="item.key"
          class="group/railitem relative shrink-0"
        >
          <Button
            v-if="item.kind !== 'conversation'"
            v-tooltip.left="displayLabel(item)"
            ghost
            slate
            sm
            class="!rounded-full transition-all duration-[250ms] ease-out active:!scale-95"
            :class="{ 'bg-n-alpha-2': isItemActive(item) }"
            :icon="displayIcon(item)"
            :data-testid="`shortcut-rail-item-${item.key}`"
            :aria-label="displayLabel(item)"
            @click="go(item)"
          />
          <button
            v-else
            v-tooltip.left="itemLabel(item)"
            type="button"
            class="relative flex size-8 items-center justify-center rounded-full text-n-slate-11 transition-all duration-[250ms] ease-out hover:bg-n-alpha-2 hover:text-n-slate-12 active:scale-95"
            :class="{ 'bg-n-alpha-2': isItemActive(item) }"
            :data-testid="`shortcut-rail-item-${item.key}`"
            :aria-label="itemLabel(item)"
            @click="go(item)"
          >
            <Avatar
              v-if="!item.thumbnail"
              :name="displayLabel(item)"
              :size="32"
            />
            <img
              v-else
              :src="item.thumbnail"
              :alt="displayLabel(item)"
              class="size-8 rounded-full object-cover"
            />
            <span
              v-if="unreadCounts[item.key]"
              class="pointer-events-none absolute -bottom-0.5 z-20 inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-n-blue-9 px-1 text-[10px] font-bold tabular-nums text-white ltr:-right-0.5 rtl:-left-0.5"
              data-testid="shortcut-rail-unread"
              aria-hidden="true"
            >
              {{ unreadBadge(unreadCounts[item.key]) }}
            </span>
          </button>
          <button
            type="button"
            class="absolute -right-0.5 -top-0.5 flex size-3.5 items-center justify-center rounded-full border border-n-strong bg-n-solid-1 text-n-slate-11 opacity-0 shadow-sm transition-opacity hover:bg-n-ruby-9 hover:text-white group-hover/railitem:opacity-100"
            :aria-label="t('DOCK.REMOVE', { name: displayLabel(item) })"
            :data-testid="`shortcut-rail-remove-${item.key}`"
            @click.stop="removeItem(item.key)"
          >
            <Icon icon="i-ri-close-line" class="size-2.5 shrink-0" />
          </button>
        </span>
      </div>

      <div class="relative mt-1 shrink-0 px-1">
        <div
          v-if="isAddOpen"
          data-testid="shortcut-rail-catalog"
          class="absolute right-full z-50 mr-2 w-[22.5rem] overflow-y-auto rounded-[var(--workspace-radius,18px)] border border-n-weak bg-n-solid-1 p-3.5"
          :class="catalogOpensUp ? 'bottom-0' : 'top-0'"
          :style="{ maxHeight: `${catalogMaxHeightPx}px` }"
        >
          <div class="mb-2 flex items-center justify-between gap-2">
            <b class="text-[13px] font-medium text-n-slate-12">
              {{ t('DOCK.ADD_TITLE') }}
            </b>
            <button
              type="button"
              data-testid="shortcut-rail-catalog-close"
              class="flex size-6 shrink-0 items-center justify-center rounded-full text-n-slate-11 hover:bg-n-alpha-2 hover:text-n-slate-12"
              :aria-label="t('DOCK.CLOSE')"
              @click="closeAddPanel"
            >
              <Icon icon="i-ri-close-line" class="size-4 shrink-0" />
            </button>
          </div>
          <div class="grid grid-cols-2 gap-1.5">
            <button
              v-for="entry in catalog"
              :key="entry.key"
              type="button"
              :data-testid="`shortcut-rail-catalog-${entry.key}`"
              class="group/cat flex min-h-11 items-center gap-2 rounded-xl px-2 py-1.5 text-left text-xs transition-colors hover:bg-n-alpha-2"
              :class="entry.inDock ? 'text-n-blue-11' : 'text-n-slate-12'"
              :title="
                entry.inDock
                  ? t('DOCK.REMOVE', { name: entry.label })
                  : entry.label
              "
              @click="
                entry.inDock ? removeItem(entry.key) : onAddFromCatalog(entry)
              "
            >
              <span
                class="flex size-7 shrink-0 items-center justify-center rounded-lg bg-n-alpha-2"
              >
                <Icon :icon="entry.icon" class="size-4 text-n-slate-11" />
              </span>
              <span class="min-w-0 flex-1">
                <span class="block leading-tight">{{ entry.label }}</span>
                <span
                  v-if="entry.adminOnly"
                  class="mt-0.5 inline-block rounded bg-n-alpha-2 px-1 text-[8px] font-bold leading-none text-n-slate-11"
                >
                  {{ t('DOCK.ADMIN_BADGE') }}
                </span>
              </span>
              <span
                v-if="entry.inDock"
                class="i-ri-check-line size-4 shrink-0 text-n-blue-11 group-hover/cat:hidden"
              />
              <span
                v-if="entry.inDock"
                class="i-ri-close-line hidden size-4 shrink-0 text-n-ruby-11 group-hover/cat:block"
              />
            </button>
          </div>
          <p class="m-0 mt-2.5 text-[11px] leading-snug text-n-slate-11">
            {{ t('DOCK.OWNERSHIP_NOTE') }}
          </p>
        </div>
        <Button
          v-tooltip.left="t('DOCK.ADD_TITLE')"
          ghost
          slate
          sm
          class="!rounded-full transition-all duration-[250ms] ease-out active:!scale-95"
          :class="{ 'bg-n-alpha-2': isAddOpen }"
          icon="i-ph-plus-bold"
          data-testid="shortcut-rail-add"
          :aria-label="t('DOCK.ADD_TITLE')"
          :aria-expanded="isAddOpen"
          @click="toggleAddPanel"
        />
      </div>
    </div>
  </aside>
</template>
